import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import useSWRInfinite from 'swr/infinite';
import { useQueryFiltersStore } from '@/stores';
import { Button } from '@/components/ui';
import { Popover } from '@/components/Popover';
import { Checkbox, SearchInput } from '@/components';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { SEARCH_DEBOUNCE_MS } from '@/constants/debounce';
import { FullAlertRule } from '@/types/alert';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';
import { useDebounce, useInfiniteScroll } from '@/hooks';

const PAGE_SIZE = 12;

interface AlertRulesMenuProps {
    onClose?: () => void;
}

export function AlertRulesMenu({ onClose }: AlertRulesMenuProps = {}) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const {
        selectedAlertRuleIds,
        setAlertRuleSelection,
        clearAlertRuleSelection,
    } = useQueryFiltersStore();
    const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(
        new Set(selectedAlertRuleIds),
    );
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(
        searchQuery.trim(),
        SEARCH_DEBOUNCE_MS,
    );
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const handleContainerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node);
    }, []);

    const getKey = useCallback(
        (
            pageIndex: number,
            previousPageData: { items: FullAlertRule[] } | null,
        ): [string, string, number] | null => {
            if (previousPageData && previousPageData.items.length === 0) {
                return null;
            }
            return [
                CACHE_KEYS.ALERT_RULES_ALL_PAGES,
                debouncedSearchQuery,
                pageIndex,
            ];
        },
        [debouncedSearchQuery],
    );

    const { data, isLoading, error, size, setSize, isValidating } =
        useSWRInfinite(
            getKey,
            ([, search, pageIndex]) =>
                EngineService.getAlertRules({
                    page: pageIndex + 1,
                    size: PAGE_SIZE,
                    ...(search ? { search } : {}),
                }),
            {
                revalidateOnFocus: false,
                revalidateFirstPage: false,
                revalidateIfStale: false,
            },
        );

    useEffect(() => {
        void setSize(1);
    }, [debouncedSearchQuery, setSize]);

    const alertRules = useMemo<FullAlertRule[]>(
        () => (data ? data.flatMap((page) => page.items) : []),
        [data],
    );

    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);

    const isLoadingMore = isValidating && size > 1;

    const loadMore = useCallback(() => {
        if (!isValidating && hasMore) {
            void setSize(size + 1);
        }
    }, [isValidating, hasMore, setSize, size]);

    useInfiniteScroll(container, {
        onLoadMore: loadMore,
        isLoading: isLoadingMore,
        hasMore,
    });

    const allSelected = useMemo(
        () =>
            alertRules.length > 0 &&
            alertRules.every((rule) => localSelectedIds.has(rule.id)),
        [alertRules, localSelectedIds],
    );

    const toggleLocalSelection = (alertRuleId: string) => {
        setLocalSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(alertRuleId)) {
                newSet.delete(alertRuleId);
            } else {
                newSet.add(alertRuleId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        setLocalSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (allSelected) {
                alertRules.forEach((rule) => {
                    newSet.delete(rule.id);
                });
            } else {
                alertRules.forEach((rule) => {
                    newSet.add(rule.id);
                });
            }
            return newSet;
        });
    };

    const handleClear = () => {
        setLocalSelectedIds(new Set());
        clearAlertRuleSelection();
        onClose?.();
    };

    const handleApply = () => {
        setAlertRuleSelection(localSelectedIds);
        onClose?.();
    };

    return (
        <Popover className="h-[400px] w-[400px] max-w-[calc(100vw-40px)] gap-0 p-0">
            <div className="flex flex-col flex-1 overflow-hidden">
                <div className="flex flex-col p-4 border-b-[0.5px] border-grey-600">
                    <div className="flex items-center gap-4 mb-4">
                        <SearchInput
                            type="text"
                            placeholder={t('alertRules.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            containerClassName="flex-1"
                        />
                    </div>

                    <Checkbox
                        checked={allSelected}
                        onChange={handleSelectAll}
                        label={t('alertRules.selectAll', {
                            count: alertRules.length,
                        })}
                    />
                </div>

                <div
                    ref={handleContainerRef}
                    className="flex-1 overflow-y-auto p-4"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-grey-300">
                                {t('alertRules.loading')}
                            </p>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-red-400">
                                {t('alertRules.loadError')}
                            </p>
                        </div>
                    ) : alertRules.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-grey-300">
                                {t('alertRules.noResults')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {alertRules.map((rule) => (
                                <Checkbox
                                    key={rule.id}
                                    checked={localSelectedIds.has(rule.id)}
                                    onChange={() =>
                                        toggleLocalSelection(rule.id)
                                    }
                                    label={getAlertRuleDisplayName(rule)}
                                />
                            ))}
                            {isLoadingMore && (
                                <div className="flex justify-center py-2">
                                    <p className="text-grey-300 text-sm">
                                        {t('page.loadingMore')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between p-4 border-t-[0.5px] border-grey-600">
                <Button variant="ghost" onClick={handleClear}>
                    {t('button.clear')}
                </Button>
                <Button variant="secondary" onClick={handleApply}>
                    {t('button.apply')}
                </Button>
            </div>
        </Popover>
    );
}
